'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.ts';
import { useListCatalogQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { CatalogSearchInput, SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.ts';
import { GiftCatalogSortEnum, useGiftCatalogQueryParams } from '../../../utils/query-params.tsx';

// ─── Sort Mapping ────────────────────────────────────────────────────────────

export const getSearchSort = (sortBy: GiftCatalogSortEnum): {
  sort?: 'pageTitle' | 'catalogAmount' | 'datePublished' | 'dateModified';
  sortDir?: 'asc' | 'desc';
  boostFundPromotion?: string;
} => {
  switch (sortBy) {
    case GiftCatalogSortEnum.enum.mostNeeded:
      {
        return {
          boostFundPromotion: 'mostNeeded'
        };
      }
    case GiftCatalogSortEnum.enum.priceLowToHigh:
      {
        return {
          sort: 'catalogAmount',
          sortDir: 'asc'
        };
      }
    case GiftCatalogSortEnum.enum.priceHighToLow:
      {
        return {
          sort: 'catalogAmount',
          sortDir: 'desc'
        };
      }
    case GiftCatalogSortEnum.enum.alphabeticalAZ:
      {
        return {
          sort: 'pageTitle',
          sortDir: 'asc'
        };
      }
    case GiftCatalogSortEnum.enum.alphabeticalZA:
      {
        return {
          sort: 'pageTitle',
          sortDir: 'desc'
        };
      }
    default:
      {
        return {
          sort: 'pageTitle',
          sortDir: 'asc'
        };
      }
  }
};

// ─── Search Input Builder ────────────────────────────────────────────────────

export const buildCatalogSearchInput = ({
  currentPage,
  selectedCategoryFilters,
  promotionFilter,
  boostPromotion,
  priceMin,
  priceMax,
  sortConfig
}: {
  currentPage: number;
  selectedCategoryFilters: string[];
  promotionFilter: string | undefined;
  boostPromotion: string | undefined;
  priceMin: number;
  priceMax: number;
  sortConfig: ReturnType<typeof getSearchSort>;
}): CatalogSearchInput => ({
  offset: (currentPage - 1) * generalConfig.catalogPerPage,
  perPage: generalConfig.catalogPerPage,
  fundCategories: selectedCategoryFilters.length > 0 ? selectedCategoryFilters : undefined,
  fundPromotion: promotionFilter || undefined,
  boostFundPromotion: boostPromotion || undefined,
  priceMin,
  priceMax,
  sort: sortConfig.sort,
  sortDir: sortConfig.sortDir
});

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useGiftCatalogCore = () => {
  const $ = _c(57);
  const [isCategoryOpen, setIsCategoryOpen] = useState(true);
  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [allResults, setAllResults] = useState(t0);
  const {
    giftCatalogFilters,
    setGiftCatalogFilters
  } = useGiftCatalogQueryParams();
  const selectedCategoryFilters = giftCatalogFilters.category;
  const selectedPromotion = giftCatalogFilters.promotion;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [generalConfig.catalogFilterMin, generalConfig.catalogFilterMax];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [priceRange, setPriceRange] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== giftCatalogFilters.priceMax || $[3] !== giftCatalogFilters.priceMin) {
    t2 = () => {
      setPriceRange([giftCatalogFilters.priceMin ?? generalConfig.catalogFilterMin, giftCatalogFilters.priceMax ?? generalConfig.catalogFilterMax]);
    };
    t3 = [giftCatalogFilters.priceMin, giftCatalogFilters.priceMax];
    $[2] = giftCatalogFilters.priceMax;
    $[3] = giftCatalogFilters.priceMin;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setCurrentPage(1);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== giftCatalogFilters.category || $[8] !== giftCatalogFilters.priceMax || $[9] !== giftCatalogFilters.priceMin || $[10] !== giftCatalogFilters.sortby) {
    t5 = [giftCatalogFilters.category, giftCatalogFilters.priceMin, giftCatalogFilters.priceMax, giftCatalogFilters.sortby];
    $[7] = giftCatalogFilters.category;
    $[8] = giftCatalogFilters.priceMax;
    $[9] = giftCatalogFilters.priceMin;
    $[10] = giftCatalogFilters.sortby;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== currentPage || $[13] !== giftCatalogFilters.priceMax || $[14] !== giftCatalogFilters.priceMin || $[15] !== giftCatalogFilters.sortby || $[16] !== selectedCategoryFilters || $[17] !== selectedPromotion) {
    const sortConfig = getSearchSort(giftCatalogFilters.sortby as GiftCatalogSortEnum);
    const promotionFilter = selectedPromotion || undefined;
    const boostPromotion = sortConfig.boostFundPromotion;
    const minPrice = giftCatalogFilters.priceMin ?? generalConfig.catalogFilterMin;
    const maxPrice = giftCatalogFilters.priceMax ?? generalConfig.catalogFilterMax;
    t6 = buildCatalogSearchInput({
      currentPage,
      selectedCategoryFilters,
      promotionFilter,
      boostPromotion,
      priceMin: minPrice,
      priceMax: maxPrice,
      sortConfig
    });
    $[12] = currentPage;
    $[13] = giftCatalogFilters.priceMax;
    $[14] = giftCatalogFilters.priceMin;
    $[15] = giftCatalogFilters.sortby;
    $[16] = selectedCategoryFilters;
    $[17] = selectedPromotion;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const searchInput = t6;
  const {
    data: searchResults,
    isLoading,
    error
  } = useListCatalogQuery(searchInput);
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      perPage: 1
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const catalogFacetQuery = useListCatalogQuery(t7);
  const {
    data: categoryData
  } = catalogFacetQuery;
  let t8;
  if ($[20] !== categoryData?.facets) {
    const facetCategories = categoryData?.facets.filter(_temp) || [];
    t8 = facetCategories[0]?.values.map(_temp2) || [];
    $[20] = categoryData?.facets;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const categories = t8;
  let t9;
  if ($[22] !== setGiftCatalogFilters) {
    t9 = sliderRange => {
      const newMin = sliderRange.newValue[0];
      const newMax = sliderRange.newValue[1];
      setGiftCatalogFilters(prev => ({
        ...prev,
        priceMin: newMin === generalConfig.catalogFilterMin ? null : newMin,
        priceMax: newMax === generalConfig.catalogFilterMax ? null : newMax
      }));
    };
    $[22] = setGiftCatalogFilters;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handlePriceChange = t9;
  let t10;
  if ($[24] !== setGiftCatalogFilters) {
    t10 = selected => {
      setGiftCatalogFilters(prev_0 => ({
        ...prev_0,
        category: selected
      }));
    };
    $[24] = setGiftCatalogFilters;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleActiveFilterChange = t10;
  let t11;
  if ($[26] !== setGiftCatalogFilters) {
    t11 = newSort => {
      setGiftCatalogFilters(prev_1 => ({
        ...prev_1,
        sortby: newSort
      }));
    };
    $[26] = setGiftCatalogFilters;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const handleSortChange = t11;
  let t12;
  let t13;
  if ($[28] !== currentPage || $[29] !== searchResults) {
    t12 = () => {
      if (!searchResults?.items) {
        return;
      }
      const newResults = searchResults.items.map(extractArgsSearchResult);
      if (currentPage === 1) {
        setAllResults(newResults);
      } else {
        setAllResults(prev_2 => [...prev_2, ...newResults]);
      }
    };
    t13 = [searchResults, currentPage];
    $[28] = currentPage;
    $[29] = searchResults;
    $[30] = t12;
    $[31] = t13;
  } else {
    t12 = $[30];
    t13 = $[31];
  }
  useEffect(t12, t13);
  const hasMore = searchResults?.totalCount && searchResults.totalCount > currentPage * generalConfig.catalogPerPage;
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setCurrentPage(_temp3);
    };
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleLoadMore = t14;
  let t15;
  if ($[33] !== setGiftCatalogFilters) {
    t15 = () => {
      setGiftCatalogFilters(null);
    };
    $[33] = setGiftCatalogFilters;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const resetAllFilters = t15;
  let t16;
  if ($[35] !== setGiftCatalogFilters) {
    t16 = () => {
      setGiftCatalogFilters(_temp4);
    };
    $[35] = setGiftCatalogFilters;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const resetPriceFilter = t16;
  const isPriceFiltered = giftCatalogFilters.priceMin !== null && giftCatalogFilters.priceMin !== generalConfig.catalogFilterMin || giftCatalogFilters.priceMax !== null && giftCatalogFilters.priceMax !== generalConfig.catalogFilterMax;
  let t17;
  if ($[37] !== allResults || $[38] !== categories || $[39] !== currentPage || $[40] !== error || $[41] !== giftCatalogFilters || $[42] !== handleActiveFilterChange || $[43] !== handlePriceChange || $[44] !== handleSortChange || $[45] !== hasMore || $[46] !== isCategoryOpen || $[47] !== isLoading || $[48] !== isMobileFiltersOpen || $[49] !== isPriceFiltered || $[50] !== isPriceOpen || $[51] !== priceRange || $[52] !== resetAllFilters || $[53] !== resetPriceFilter || $[54] !== searchResults || $[55] !== selectedCategoryFilters) {
    t17 = {
      isCategoryOpen,
      setIsCategoryOpen,
      isPriceOpen,
      setIsPriceOpen,
      isMobileFiltersOpen,
      setIsMobileFiltersOpen,
      currentPage,
      selectedCategoryFilters,
      categories,
      priceRange,
      isPriceFiltered,
      giftCatalogFilters,
      allResults,
      searchResults,
      isLoading,
      error,
      hasMore,
      handlePriceChange,
      handleActiveFilterChange,
      handleSortChange,
      handleLoadMore,
      resetAllFilters,
      resetPriceFilter
    };
    $[37] = allResults;
    $[38] = categories;
    $[39] = currentPage;
    $[40] = error;
    $[41] = giftCatalogFilters;
    $[42] = handleActiveFilterChange;
    $[43] = handlePriceChange;
    $[44] = handleSortChange;
    $[45] = hasMore;
    $[46] = isCategoryOpen;
    $[47] = isLoading;
    $[48] = isMobileFiltersOpen;
    $[49] = isPriceFiltered;
    $[50] = isPriceOpen;
    $[51] = priceRange;
    $[52] = resetAllFilters;
    $[53] = resetPriceFilter;
    $[54] = searchResults;
    $[55] = selectedCategoryFilters;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  return t17;
};
function _temp(facet) {
  return facet?.field === "taxonomy.fundCategory.keyword";
}
function _temp2(category) {
  return {
    name: category.key,
    value: category.key
  };
}
function _temp3(prev_3) {
  return prev_3 + 1;
}
function _temp4(prev_4) {
  return {
    ...prev_4,
    priceMin: null,
    priceMax: null
  };
}